'use client'

/**
 * PDP IRL (Quince IRL) PL widget — NON-DS, widget-of-widget container.
 *
 * Mirrors the legacy non-DS `InfluencerSection` atom's markup/Swiper, but instead of mapping the
 * card list to `<InfluencerCard>` internally it arranges the PL renderer's pre-rendered
 * INFLUENCER_CARD children (`renderedChildren`) inside the Swiper — the same widget-of-widget shape
 * as PRODUCT_LIST → SINGLE_PRODUCT_CARD.
 *
 * The two list-level concerns that can't live on an individual card stay here: the single batched
 * product fetch (`useInfluencerCardProducts`) and the shoppable popup (`socialSectionStateManager`).
 * They're published to each child via `InfluencerCardContext`. The section still reads the nested
 * INFLUENCER_CARD data (`props.widgets`) to build that list and to filter which children are
 * shoppable; each child renders itself from its own data.
 */

import classnames from 'classnames'
import { useCallback, useEffect, useMemo } from 'react'

import { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import influencerStyles from '@src/quince-components/src/atoms/influencerSection/influencerSection.module.scss'
import Swiper from '@src/quince-components/src/atoms/swiper'
import { ARROW_COLOR } from '@src/quince-components/src/atoms/swiper/Swiper'
import { useInfluencerCardProducts } from '@src/quince-components/src/hooks/useInfluencerCardProducts'
import { RichText as RichTextComponent } from '@src/quince-components/src/molecules'
import {
  getIndividualTagStatus,
  getInitialSelectedOptions,
  getProductTagsList,
  isNestedProduct,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import type InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import RichText from '@src/quince-site-common/src/domains/RichText'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import socialSectionStateManager from '@src/quince-site-common/src/redux/globalStateManager/SocialSectionStateManager'
import getContentContainerDetails from '@src/quince-site-common/src/utils/getContentContainerDetails'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { toRichText } from '../widget-utils'

import { InfluencerCardContextProvider, type InfluencerCardContextValue } from './influencer-card-context'
import { adaptInfluencerCard } from './influencer-section.adapter'

import type {
  InfluencerCardData,
  InfluencerSectionWidgetData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Widget.widgets is a recursive proto type TS can't resolve through Omit<Widget,'data'>;
// read nested INFLUENCER_CARD data via a narrow cast (same pattern as FAQWidget).
interface InfluencerNestedWidget {
  data?: { influencerCard?: InfluencerCardData }
}

// A card is shoppable (and therefore shown + fetched) when it carries the id this storefront mode
// resolves products by — contentfulProductId for B2B self-service, spid otherwise. Mirrors the atom.
const isShoppable = (detail: InfluencerDetails | null, isB2BSelfService: boolean): detail is InfluencerDetails =>
  detail != null && (isB2BSelfService ? !!detail.contentfulProductId : detail.spid != null)

export function InfluencerSectionWidgetLegacy(props: PLTypedWidgetProps<InfluencerSectionWidgetData>) {
  const data = props.data.influencerSectionData
  const { isB2BSelfService } = useStorefrontModeContext()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const headingRichText = RichText.makeRichTextProps(toRichText(data?.heading))
  const descriptionRichText = RichText.makeRichTextProps(toRichText(data?.description))

  const nestedWidgets = (props as unknown as { widgets?: InfluencerNestedWidget[] }).widgets
  const renderedChildren = props.renderedChildren ?? []

  // Card details aligned 1:1 with nestedWidgets (and therefore with renderedChildren), preserving
  // index so a card's rendered node can be paired with its data; non-card entries map to null.
  const allDetails = useMemo<(InfluencerDetails | null)[]>(
    () =>
      (Array.isArray(nestedWidgets) ? nestedWidgets : []).map(widget => {
        const card = widget.data?.influencerCard
        return card ? adaptInfluencerCard(card) : null
      }),
    [nestedWidgets],
  )

  // The shoppable subset drives the product fetch + popup. Memoized on a stable input so the
  // fetch effect inside useInfluencerCardProducts doesn't re-run every render.
  const filteredInfluencerDetailList = useMemo(
    () => allDetails.filter((detail): detail is InfluencerDetails => isShoppable(detail, isB2BSelfService)),
    [allDetails, isB2BSelfService],
  )

  const { isLoadingProducts, products, spidToProductMap, contentfulProductIdToProductMap } = useInfluencerCardProducts(
    filteredInfluencerDetailList,
    isB2BSelfService,
  )

  useEffect(() => {
    socialSectionStateManager.updateProductsAndLoadingState(isLoadingProducts, products)
  }, [isLoadingProducts, products])

  // Open the shoppable popup at the clicked card — identical to the legacy non-PL atom's
  // handleItemClick: pass the whole filteredInfluencerDetailList and the clicked card's index within
  // it; SocialSection renders getIrlItems(list, products) and seeks to that index. No extra logic —
  // same behavior as non-PL (including how it treats cards whose product didn't resolve).
  const openPopupAt = useCallback(
    (influencerIndex: number) => {
      const influencerDetail = filteredInfluencerDetailList[influencerIndex]
      if (!influencerDetail) return

      const product = products?.[0]
      const contentContainerDetails = getContentContainerDetails(undefined)

      if (product) {
        const selectedOptions = getInitialSelectedOptions(
          product,
          [...influencerDetail.selectedProductOptions],
          true,
          false,
          isNestedProduct(product) ? 2 : 1,
        )
        const productTags = getProductTagsList(product.tags, selectedOptions)

        trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
          context: {
            contentContainerDisplayName: contentContainerDetails.contentContainerDisplayName,
            contentContainerType: contentContainerDetails.contentContainerType,
            contentContainerName: contentContainerDetails.contentContainerName,
            productPosition: influencerIndex,
            tags: productTags,
            tag_ids: transformTagsIntoAnalyticsTags(productTags),
            ...getIndividualTagStatus(productTags),
          },
        })
      }

      socialSectionStateManager.showSocialSectionPopUp({
        influencerDetails: filteredInfluencerDetailList,
        influencerIndex,
        isLoadingProducts,
        products,
      })
    },
    [filteredInfluencerDetailList, products, isLoadingProducts],
  )

  // Shared (card-agnostic) context: the batched product maps. The popup opener is bound per-card
  // below, so each card opens its own slide regardless of (empty) card ids.
  const sharedContext = useMemo(
    () => ({ isB2BSelfService, spidToProductMap, contentfulProductIdToProductMap }),
    [isB2BSelfService, spidToProductMap, contentfulProductIdToProductMap],
  )

  // Render every shoppable card's pre-rendered node as a Swiper slide — same set the legacy atom
  // shows (filtered by spid/contentful id only). Each node is wrapped in a context provider whose
  // openPopup is bound to that card's index within filteredInfluencerDetailList, so the click opens
  // the popup at the strip index — exactly the atom's handleItemClick(index). `stripIndex` advances
  // for every shoppable card (matching the filter that builds filteredInfluencerDetailList).
  let stripIndex = -1
  const slides: JSX.Element[] = []
  allDetails.forEach((detail, index) => {
    if (!isShoppable(detail, isB2BSelfService)) return
    stripIndex += 1
    const node = renderedChildren[index]?.node
    if (!node) return
    const cardIndex = stripIndex
    const cardContext: InfluencerCardContextValue = {
      ...sharedContext,
      openPopup: () => {
        openPopupAt(cardIndex)
      },
    }
    slides.push(
      <InfluencerCardContextProvider key={index} value={cardContext}>
        {node}
      </InfluencerCardContextProvider>,
    )
  })

  if (slides.length === 0) return null

  return (
    <div className={classnames(influencerStyles.productCarouselWrap, (data?.classNames ?? []).join(' '))}>
      {/* eslint-disable-next-line i18next/no-literal-string -- layout utility classes, not copy */}
      <div className="d-flex flex-d--column align-items--center padding-h--30">
        {headingRichText && <RichTextComponent {...headingRichText} />}
        {descriptionRichText && <RichTextComponent {...descriptionRichText} />}
      </div>
      <div className={influencerStyles.influencerSliderWrapper}>
        <Swiper
          showDots={false}
          variableWidth
          showNavigator={isDesktop}
          numberOfVisibleSlides={isDesktop ? 4 : 1}
          className={influencerStyles.influencerSlider}
          arrowColor={ARROW_COLOR.WHITE}
          id="influencerSection"
          virtualOptions={{ enabled: true }}
          // @i18n-ignore
          tracker="influencer-section"
        >
          {slides}
        </Swiper>
      </div>
    </div>
  )
}
